<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="3" class="mb-1">
            <label>Неделя</label>
            <v-select
              v-model="filters.week_id"
              :options="weeks"
              label="name"
              :reduce="w => w.id"
              placeholder="Все недели"
            />
          </b-col>
          <b-col cols="12" md="3" class="mb-1">
            <label>Ведущий</label>
            <v-select
              v-model="filters.host"
              :options="hosts"
              placeholder="Все ведущие"
            />
          </b-col>
          <b-col cols="12" md="3" class="mb-1">
            <label>Статус</label>
            <b-form-select v-model="filters.status" :options="statusOptions" />
          </b-col>
          <b-col cols="12" md="3" class="mb-1">
            <label>Источник</label>
            <b-form-select v-model="filters.source" :options="sourceOptions" />
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12" md="3" class="mb-1">
            <label>С</label>
            <flat-pickr v-model="filters.from" class="form-control" :config="datePickerConfig" placeholder="Не задано" />
          </b-col>
          <b-col cols="12" md="3" class="mb-1">
            <label>По</label>
            <flat-pickr v-model="filters.to" class="form-control" :config="datePickerConfig" placeholder="Не задано" />
          </b-col>
          <b-col cols="12" md="4" class="mb-1">
            <label>Поиск</label>
            <b-form-input v-model="filters.q" placeholder="Тема, пакет, ведущий..." @keyup.enter="applyFilters" />
          </b-col>
          <b-col cols="12" md="2" class="d-flex align-items-end mb-1">
            <b-button variant="primary" block @click="applyFilters">Применить</b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-overlay :show="isLoading" rounded="sm">
      <b-card no-body>
        <b-card-body>
          <div class="d-flex justify-content-between align-items-center mb-1">
            <small class="text-muted">
              <template v-if="syncedAt">Данные из ПФ на {{ formatDateTime(syncedAt) }}</template>
            </small>
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="isSyncing"
              title="Подтянуть из Планфикса статусы, игроков и оплаты свежих недель (~минута)"
              @click="requestSync"
            >
              <b-spinner v-if="isSyncing" small class="mr-50" />
              {{ isSyncing ? 'Обновляется…' : 'Обновить из ПФ' }}
            </b-button>
          </div>
          <vue-good-table
            mode="remote"
            :rows="games"
            :columns="columns"
            :total-rows="meta.total"
            :pagination-options="{ enabled: true, perPage, perPageDropdown: [25, 50, 100], setCurrentPage: page }"
            :sort-options="{ enabled: false }"
            @on-page-change="onPageChange"
            @on-per-page-change="onPerPageChange"
          >
            <template slot="emptystate">
              <div class="text-center text-muted py-2">Игры не найдены</div>
            </template>
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'scheduled_at'">
                {{ formatDateTime(props.row.scheduled_at) }}
                <small v-if="props.row.is_rescheduled" class="text-warning d-block">
                  факт {{ formatDateTime(props.row.actual_start_at) }}
                </small>
              </span>
              <span v-else-if="props.column.field === 'week_name'">{{ props.row.week_name || '—' }}</span>
              <span v-else-if="props.column.field === 'topic_name'">{{ props.row.topic_name || '—' }}</span>
              <span v-else-if="props.column.field === 'pack_name'">{{ props.row.pack_name || '—' }}</span>
              <span v-else-if="props.column.field === 'host_name'">
                {{ props.row.host_name || '—' }}
                <span v-if="props.row.is_host_changed" class="text-warning">
                  → {{ props.row.actual_host_name }}
                </span>
              </span>
              <span v-else-if="props.column.field === 'players_count'">
                {{ props.row.players_count != null ? props.row.players_count : '—' }}
              </span>
              <span v-else-if="props.column.field === 'payments_sum'">{{ formatRub(props.row.payments_sum) }}</span>
              <span v-else-if="props.column.field === 'status'">
                <plan-fact-status-badge :game="props.row" />
              </span>
              <span v-else-if="props.column.field === 'task_url'">
                <b-link v-if="props.row.task_url" :href="props.row.task_url" target="_blank" rel="noopener noreferrer">
                  задача
                </b-link>
                <span v-else class="text-muted">—</span>
              </span>
              <span v-else-if="props.column.field === 'feedback'">
                <b-button
                  v-if="props.row.planfix_task_id && props.row.planfix_status_id === PLANFIX_STATUS_COMPLETED"
                  size="sm"
                  variant="outline-primary"
                  :disabled="sendingTaskId !== null"
                  title="Отправить игрокам ссылки на обратную связь в Telegram"
                  @click="sendFeedback(props.row)"
                >
                  <b-spinner v-if="sendingTaskId === props.row.planfix_task_id" small />
                  <span v-else>ОС</span>
                </b-button>
                <span v-else class="text-muted">—</span>
              </span>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>
        </b-card-body>
      </b-card>
    </b-overlay>

    <b-modal
      v-model="feedbackModalVisible"
      title="Ссылки на обратную связь"
      ok-only
      ok-title="Закрыть"
    >
      <p v-if="feedbackResult.link" class="mb-1">
        Ссылка: <b-link :href="feedbackResult.link" target="_blank" rel="noopener noreferrer">{{ feedbackResult.link }}</b-link>
      </p>
      <p class="mb-1">Отправлено: {{ feedbackResult.sentCount }} из {{ feedbackResult.results.length }}</p>
      <ul class="pl-1 mb-0" style="list-style: none;">
        <li v-for="(item, index) in feedbackResult.results" :key="index">
          <span v-if="item.status === 'sent'" class="text-success">✅ {{ item.name }} ({{ item.destination }})</span>
          <span v-else-if="item.status === 'failed'" class="text-danger">❌ {{ item.name }} ({{ item.destination }}) — {{ item.reason }}</span>
          <span v-else class="text-muted">⏭ {{ item.name }} — {{ item.reason }}</span>
        </li>
      </ul>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BFormSelect, BFormInput, BButton, BOverlay, BLink, BModal, BSpinner,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import { ref, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import gameStoreModule from '@/views/game/gameStoreModule'
import PlanFactStatusBadge from '@/views/game/components/PlanFactStatusBadge.vue'
import { STATUS_OPTIONS, SOURCE_OPTIONS, formatDateTime } from '@/views/game/planFact'
import { formatRub } from '@/views/finance/utils'

const STORE_MODULE_NAME = 'app-games'

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormSelect,
    BFormInput,
    BButton,
    BOverlay,
    BLink,
    BModal,
    BSpinner,
    VueGoodTable,
    vSelect,
    flatPickr,
    PlanFactStatusBadge,
  },
  setup(props, { root }) {
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, gameStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const toast = useToast()
    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })
    const showSuccess = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'CheckIcon', variant: 'success' },
    })

    const datePickerConfig = { dateFormat: 'Y-m-d', altInput: true, altFormat: 'd.m.Y' }

    const statusOptions = [{ value: '', text: 'Все статусы' }, ...STATUS_OPTIONS]
    const sourceOptions = [{ value: '', text: 'Все источники' }, ...SOURCE_OPTIONS]

    const weeks = ref([])
    const hosts = ref([])
    const games = ref([])
    const meta = ref({
      total: 0, per_page: 25, current_page: 1, last_page: 1,
    })
    const isLoading = ref(false)
    const page = ref(1)
    const perPage = ref(25)

    const filters = ref({
      week_id: null,
      host: null,
      status: '',
      source: '',
      from: '',
      to: '',
      q: '',
    })

    const columns = [
      { label: 'Дата/время', field: 'scheduled_at' },
      { label: 'Неделя', field: 'week_name' },
      { label: 'Тема', field: 'topic_name' },
      { label: 'Пакет', field: 'pack_name' },
      { label: 'Ведущий', field: 'host_name' },
      { label: 'Игроков', field: 'players_count' },
      { label: 'Оплата', field: 'payments_sum' },
      { label: 'Статус', field: 'status' },
      { label: 'Задача', field: 'task_url' },
      { label: 'ОС', field: 'feedback' },
    ]

    // Статус Planfix «Выполненная»: только из него доступна рассылка ОС,
    // после отправки бэкенд переводит задачу в «Завершённая»
    const PLANFIX_STATUS_FINISHED = 3
    const PLANFIX_STATUS_COMPLETED = 6

    const sendingTaskId = ref(null)
    const feedbackModalVisible = ref(false)
    const feedbackResult = ref({ link: '', sentCount: 0, results: [] })

    const sendFeedback = async row => {
      const gameLabel = `${row.topic_name || ''} ${formatDateTime(row.scheduled_at)}`.trim()
      const confirmed = await root.$bvModal.msgBoxConfirm(
        `Отправить игрокам ссылки на обратную связь по игре «${gameLabel}»? Задача будет переведена в статус «Завершённая».`,
        {
          title: 'Отправка ссылок на ОС',
          okTitle: 'Отправить',
          cancelTitle: 'Отмена',
          okVariant: 'primary',
          centered: true,
        },
      )
      if (!confirmed) return

      sendingTaskId.value = row.planfix_task_id
      store.dispatch('app-games/sendFeedbackRequests', { task_id: row.planfix_task_id })
        .then(response => {
          feedbackResult.value = response.data.data
          feedbackModalVisible.value = true
          if (feedbackResult.value.sentCount > 0) {
            const game = games.value.find(g => g.planfix_task_id === row.planfix_task_id)
            if (game) game.planfix_status_id = PLANFIX_STATUS_FINISHED
            showSuccess(`Отправлено: ${feedbackResult.value.sentCount}`)
          } else {
            showError('Не отправлено никому — у игроков нет Telegram-контактов')
          }
        })
        .catch(error => showError((error.response && error.response.data && error.response.data.message) || 'Ошибка отправки ссылок на ОС'))
        .finally(() => {
          sendingTaskId.value = null
        })
    }

    // Кнопка «Обновить из ПФ»: ставит флаг, синк выполняет планировщик раз в минуту —
    // поллим статус, пока не сменится synced_at (как кнопка «Обновить» в Финансах)
    const syncedAt = ref(null)
    const isSyncing = ref(false)
    let syncPollTimer = null

    const stopSyncPolling = () => {
      if (syncPollTimer) {
        clearInterval(syncPollTimer)
        syncPollTimer = null
      }
      isSyncing.value = false
    }

    const fetchSyncStatus = () => store.dispatch('app-games/fetchPlanfixSyncStatus')
      .then(response => response.data.data)

    const requestSync = () => {
      isSyncing.value = true
      const syncedAtBefore = syncedAt.value

      store.dispatch('app-games/requestPlanfixSync')
        .then(() => {
          const startedAt = Date.now()
          syncPollTimer = setInterval(() => {
            if (Date.now() - startedAt > 5 * 60 * 1000) {
              stopSyncPolling()
              showError('Синк не завершился за 5 минут — обновите страницу позже')
              return
            }
            fetchSyncStatus()
              .then(status => {
                if (status.synced_at !== syncedAtBefore && !status.refresh_requested) {
                  syncedAt.value = status.synced_at
                  stopSyncPolling()
                  fetchGames()
                  showSuccess('Данные обновлены из Планфикса')
                }
              })
              .catch(() => {})
          }, 5000)
        })
        .catch(error => {
          stopSyncPolling()
          showError((error.response && error.response.data && error.response.data.message) || 'Не удалось запросить обновление')
        })
    }

    onUnmounted(stopSyncPolling)

    fetchSyncStatus()
      .then(status => {
        syncedAt.value = status.synced_at
        // Кто-то уже запросил обновление (или страница переоткрыта во время синка) — продолжаем ждать
        if (status.refresh_requested) requestSync()
      })
      .catch(() => {})

    const fetchGames = () => {
      isLoading.value = true
      const params = { page: page.value, per_page: perPage.value }
      if (filters.value.week_id) params.week_id = filters.value.week_id
      if (filters.value.host) params.host = filters.value.host
      if (filters.value.status) params.status = filters.value.status
      if (filters.value.source) params.source = filters.value.source
      if (filters.value.from) params.from = filters.value.from
      if (filters.value.to) params.to = filters.value.to
      if (filters.value.q) params.q = filters.value.q

      store.dispatch('app-games/fetchPlanfixGames', params)
        .then(response => {
          games.value = response.data.data || []
          meta.value = response.data.meta || meta.value
        })
        .catch(error => showError((error.response && error.response.data && error.response.data.message) || 'Ошибка загрузки игр'))
        .finally(() => {
          isLoading.value = false
        })
    }

    const applyFilters = () => {
      page.value = 1
      fetchGames()
    }

    const onPageChange = params => {
      page.value = params.currentPage
      fetchGames()
    }

    const onPerPageChange = params => {
      perPage.value = params.currentPerPage
      page.value = 1
      fetchGames()
    }

    store.dispatch('app-games/fetchGameWeeks')
      .then(response => {
        weeks.value = response.data.data
      })
      .catch(() => showError('Ошибка загрузки недель'))

    store.dispatch('app-games/fetchPlanfixGameHosts')
      .then(response => {
        hosts.value = response.data.data
      })
      .catch(() => showError('Ошибка загрузки ведущих'))

    fetchGames()

    return {
      filters,
      statusOptions,
      sourceOptions,
      datePickerConfig,
      weeks,
      hosts,
      games,
      meta,
      isLoading,
      page,
      perPage,
      columns,
      applyFilters,
      onPageChange,
      onPerPageChange,
      formatDateTime,
      formatRub,
      PLANFIX_STATUS_COMPLETED,
      sendingTaskId,
      feedbackModalVisible,
      feedbackResult,
      sendFeedback,
      syncedAt,
      isSyncing,
      requestSync,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
